import dayjs from 'libs/dayjs';

// Mapea el estado del backend al vocabulario del informe (consistente con
// MantencionDocument.jsx: EXITOSO→FUNCIONANDO, RECHAZADO→FALLA).
function mapEstado(estado) {
  if (estado === 'RECHAZADO') return 'FALLA';
  if (estado === 'EXITOSO') return 'FUNCIONANDO';
  return 'NO APLICA';
}

// Red de seguridad de paridad: la data histórica (previa a unificar-captura) no
// tiene `actividad_hallazgo` estructurado, pero sí guarda el texto del técnico en
// los campos legacy `observacion`/`sugerencia` de la respuesta. Cuando no hay
// hallazgos, derivamos el detalle de ese texto para no perderlo. Solo aplica a
// FALLA: las celdas conformes mantienen 'Estado normal' porque su texto legacy es
// ruido de conformidad (ver limpieza de ruido en el pipeline de migración).
function detalleFromLegacy(r, estado) {
  if (estado !== 'FALLA') return 'Estado normal';
  const obs = (r.observacion || '').trim();
  const sug = (r.sugerencia || '').trim();
  const parts = [];
  if (obs) parts.push(obs);
  if (sug) parts.push(`Sugerencia: ${sug}`);
  return parts.length ? parts.join(' · ') : 'Con problemas';
}

// Pivota actividades (cada una con Respuestas por ascensor) a la estructura
// sectorizada que consumen inspection-matrix / incidence-summary.
function buildInspeccion(actividades) {
  const bySector = new Map();
  for (const act of actividades || []) {
    const sector = act?.Categoria?.nombre || 'Sin sector';
    // Excluye ítems sin estado elegido (null/'') y los "No aplica": solo se
    // muestran EXITOSO/RECHAZADO. El estado nace null hasta que el técnico marca.
    const respuestas = (act?.Respuestas || []).filter((r) => r.estado && r.estado !== 'NA');
    if (respuestas.length === 0) continue;
    const cells = {};
    for (const r of respuestas) {
      const estado = mapEstado(r.estado);
      const hs = Array.isArray(r.hallazgos)
        ? r.hallazgos.filter((h) => (h.descripcion || '').trim() !== '')
        : [];
      const detalle = hs.length
        ? hs.map((h) => h.descripcion.trim()).join(' · ')
        : detalleFromLegacy(r, estado);
      cells[r?.Ascensor?.nombre || '—'] = { estado, detalle, hallazgos: hs };
    }
    if (!bySector.has(sector)) bySector.set(sector, []);
    bySector.get(sector).push({ act: act.actividad, cells });
  }
  return Array.from(bySector.entries()).map(([sector, items]) => ({ sector, items }));
}

export function mapInformeData(input) {
  const { mantencion = {}, Cliente = {}, Responsable = {}, actividades = [] } = input || {};
  const inspeccion = buildInspeccion(actividades);

  // Métricas
  const eqSet = new Set();
  let nFalla = 0;
  let nCeldas = 0;
  for (const sec of inspeccion) {
    for (const item of sec.items) {
      for (const [asc, cell] of Object.entries(item.cells)) {
        eqSet.add(asc);
        nCeldas += 1;
        if (cell.estado === 'FALLA') nFalla += 1;
      }
    }
  }
  const nActividades = inspeccion.reduce((n, s) => n + s.items.length, 0);
  const equipos = Array.from(eqSet);

  return {
    // embebidas de cabecera
    comunidad: Cliente.name || '',
    direccion: [Cliente.address, Cliente.commune].filter(Boolean).join(', '),
    mail: Cliente.email || '',
    especialista: Responsable.fullname || '',
    tipo_servicio: 'Mantención preventiva',
    fecha: dayjs(mantencion.createdAt).format('DD [de] MMMM [de] YYYY'),
    equipos_label: `${equipos.length} ascensores · ${equipos.join(' / ')}`,
    numero: mantencion.id,
    // métricas
    n_equipos: equipos.length,
    n_actividades: nActividades,
    n_conformes: nCeldas - nFalla,
    n_incidencias: nFalla,
    // textos
    observaciones:
      mantencion.observaciones && mantencion.observaciones.trim() !== ''
        ? mantencion.observaciones
        : 'Sin observaciones generales.',
    // dataset de inspección
    inspeccion,
  };
}
